/**
 * Cross-link chips shown on every directory finding card so users can
 * pivot between the three evidence views:
 *   • PERSON pivot  (Employees)         — "Affected users (N)"
 *   • CONTROL pivot (Audit controls)    — "Linked controls (N)"
 *
 * Each chip is intentionally tiny: counts + a single click pivots the
 * user to the appropriate surface. No drawer / modal / hover-card.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Users, ListChecks, ExternalLink, ChevronRight, ChevronDown } from 'lucide-react';
import { Badge } from './ui/badge';
import { openControlDetail } from './ControlDetailModalHost';

const Pill = ({ children, onClick, disabled, testId, icon: Icon }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    data-testid={testId}
    className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border transition-colors ${
      disabled
        ? 'border-slate-200 text-slate-400 cursor-not-allowed bg-slate-50'
        : 'border-slate-300 text-slate-700 hover:bg-slate-100 hover:border-slate-400 bg-white'
    }`}
  >
    {Icon && <Icon className="w-3 h-3" />}
    {children}
    {!disabled && <ChevronRight className="w-3 h-3" />}
  </button>
);

/**
 * AffectedUsersChip — renders the finding's user-shaped `matches[]` INLINE
 * (expanded by default) so reviewers see who is affected without a click.
 * A "View in Employees" link pivots to the filtered Employees table.
 */
export const AffectedUsersChip = ({ ruleKey, matches }) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(true);
  const users = (matches || []).filter(m => m && (m.email || m.primary_email));
  if (users.length === 0) return null;

  return (
    <div className="w-full mt-1" data-testid={`affected-users-inline-${ruleKey}`}>
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => setOpen(o => !o)}
          aria-expanded={open}
          data-testid={`chip-affected-users-${ruleKey}`}
          className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-700 hover:text-slate-900"
        >
          <Users className="w-3.5 h-3.5" />
          Affected users ({users.length})
          {open ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
        </button>
        <button
          type="button"
          onClick={() => navigate(`/employees?finding=${encodeURIComponent(ruleKey || '')}`)}
          data-testid={`affected-users-view-${ruleKey}`}
          className="text-[11px] font-medium text-blue-600 hover:text-blue-700"
        >
          View in Employees →
        </button>
      </div>

      {open && (
        <div className="mt-2 border border-slate-200 rounded-lg divide-y divide-slate-100 overflow-hidden max-h-72 overflow-y-auto">
          {users.map((u, i) => {
            const email = u.email || u.primary_email;
            return (
              <div key={u.source_user_id || email || i} className="px-3 py-2" data-testid={`affected-user-row-${i}`}>
                {u.full_name && <div className="text-sm font-medium text-slate-900">{u.full_name}</div>}
                <div className="text-xs text-slate-500">{email}{u.days_since != null ? ` · ${u.days_since}d inactive` : ''}</div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const STATUS_CLS = {
  passed: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  completed: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  failed: 'bg-red-100 text-red-700 border-red-200',
  in_progress: 'bg-blue-100 text-blue-700 border-blue-200',
  insufficient_evidence: 'bg-amber-100 text-amber-700 border-amber-200',
  pending: 'bg-slate-100 text-slate-600 border-slate-200',
};

/**
 * LinkedControlsChip — hits /api/evidence-rules/{rule_key}/linked-controls
 * and renders the linked controls INLINE inside the finding card (no modal),
 * so the user sees everything at a glance. Each row opens the control in a
 * new tab to preserve the governance page context.
 */
export const LinkedControlsChip = ({ ruleKey, workspaceId }) => {
  const [controls, setControls] = useState(null); // null = unloaded, [] = empty
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(true); // inline list, expanded by default

  useEffect(() => {
    if (!ruleKey || !workspaceId) return;
    let active = true;
    setLoading(true);
    axios
      .get(`/api/evidence-rules/${encodeURIComponent(ruleKey)}/linked-controls`,
           { params: { workspace_id: workspaceId } })
      .then(r => { if (active) setControls(r.data?.controls || []); })
      .catch(() => { if (active) setControls([]); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [ruleKey, workspaceId]);

  const total = controls?.length ?? null;
  if (total === null && !loading) return null;

  const openControl = (c) => {
    // Open the full control detail as an in-page modal so the user stays
    // on the governance page (a host listens for this event).
    openControlDetail(c.control_id);
  };

  if (loading && total === null) {
    return <Pill icon={ListChecks} disabled testId={`chip-linked-controls-${ruleKey}`}>Linked controls…</Pill>;
  }
  if (!total) {
    return <Pill icon={ListChecks} disabled testId={`chip-linked-controls-${ruleKey}`}>No linked controls</Pill>;
  }

  return (
    <div className="w-full mt-1" data-testid={`linked-controls-inline-${ruleKey}`}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        data-testid={`chip-linked-controls-${ruleKey}`}
        className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-700 hover:text-slate-900"
      >
        <ListChecks className="w-3.5 h-3.5" />
        Linked controls ({total})
        {open ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
      </button>

      {open && (
        <div className="mt-2 border border-slate-200 rounded-lg divide-y divide-slate-100 overflow-hidden max-h-80 overflow-y-auto">
          {controls.map((c) => {
            const statusCls = STATUS_CLS[c.status] || STATUS_CLS.pending;
            return (
              <div
                key={`${c.audit_id}:${c.control_id}`}
                className="flex items-center justify-between gap-3 px-3 py-2 hover:bg-slate-50"
                data-testid={`linked-control-row-${c.control_id}`}
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-baseline gap-2 flex-wrap">
                    {c.control_ref && (
                      <code className="text-[11px] font-mono text-slate-500 bg-slate-100 px-1 rounded">
                        {c.control_ref}
                      </code>
                    )}
                    <span className="text-sm font-medium text-slate-900 truncate">
                      {c.control_title || '(untitled control)'}
                    </span>
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-2 flex-wrap">
                    <span className="truncate">{c.audit_name || '(unnamed audit)'}</span>
                    {c.framework && (<><span>·</span><span>{c.framework}</span></>)}
                    {c.status && (
                      <>
                        <span>·</span>
                        <Badge variant="outline" className={`text-[10px] px-1.5 py-0 h-4 capitalize ${statusCls}`}>
                          {String(c.status).replace(/_/g, ' ')}
                        </Badge>
                      </>
                    )}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => openControl(c)}
                  className="shrink-0 inline-flex items-center gap-1 text-[11px] font-medium text-slate-600 border border-slate-300 rounded-md px-2 py-1 hover:bg-white hover:border-slate-400 transition-colors"
                  data-testid={`linked-control-open-${c.control_id}`}
                  title="Open control details"
                >
                  Open <ChevronRight className="w-3 h-3" />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

/**
 * Convenience wrapper — renders both chips with consistent spacing.
 */
export const FindingCrossLinks = ({ ruleKey, matches, workspaceId, extras = null }) => (
  <div className="flex flex-col gap-2 mt-2" data-testid={`cross-links-${ruleKey}`}>
    <AffectedUsersChip ruleKey={ruleKey} matches={matches} />
    <LinkedControlsChip ruleKey={ruleKey} workspaceId={workspaceId} />
    {extras}
  </div>
);

export default FindingCrossLinks;
export { ExternalLink };
